import React, { useMemo, useState } from 'react';
import { FaTimes, FaPlus, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import '../styles/InventoryProducts.css';

const CREATE_ITEM = `
  mutation CreateItem($createItemInput: CreateItemInput!) {
    createItem(createItemInput: $createItemInput) {
      id
      name
      code
    }
  }
`;

const CATEGORIES = [
  'Chemical',
  'PPE',
  'Consumable',
  'Tool',
  'Equipment',
  'Uniform',
  'Device',
  'Vehicle',
];

const UNITS = ['Each', 'Litre', 'Box', 'Roll', 'Pair', 'Kg', 'Pack', 'Carton'];

function emptyRow() {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: '',
    category: 'Consumable',
    unit: 'Each',
    perItemCost: '',
    quantity: '0',
    reorderLevel: '5',
    supplier: '',
    description: '',
  };
}

export default function BulkAddProductsModal({ onClose, onSaved }) {
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [saving, setSaving] = useState(false);

  const filledCount = useMemo(
    () => rows.filter((r) => String(r.name || '').trim()).length,
    [rows],
  );

  const updateRow = (key, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)),
    );
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (key) => {
    setRows((prev) =>
      prev.length <= 1 ? prev : prev.filter((r) => r.key !== key),
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const toCreate = rows
      .map((r) => ({
        name: String(r.name || '').trim(),
        category: r.category,
        unit: r.unit,
        perItemCost: Number.parseFloat(r.perItemCost),
        quantity: Number.parseFloat(r.quantity),
        reorderLevel: Number.parseFloat(r.reorderLevel),
        supplier: String(r.supplier || '').trim() || undefined,
        description: String(r.description || '').trim() || undefined,
      }))
      .filter((r) => r.name);

    if (toCreate.length === 0) {
      toast.error('Add at least one product with a name');
      return;
    }

    for (const row of toCreate) {
      if (!row.category) {
        toast.error(`Category required for "${row.name}"`);
        return;
      }
      if (!row.unit) {
        toast.error(`Unit required for "${row.name}"`);
        return;
      }
      if (!Number.isFinite(row.perItemCost) || row.perItemCost < 0) {
        toast.error(`Valid cost required for "${row.name}"`);
        return;
      }
      if (!Number.isFinite(row.quantity) || row.quantity < 0) {
        toast.error(`Valid quantity required for "${row.name}"`);
        return;
      }
      if (!Number.isFinite(row.reorderLevel) || row.reorderLevel < 0) {
        toast.error(`Valid reorder level required for "${row.name}"`);
        return;
      }
    }

    setSaving(true);
    let ok = 0;
    const errors = [];
    try {
      for (const row of toCreate) {
        const full = {
          name: row.name,
          quantity: row.quantity,
          perItemCost: row.perItemCost,
          category: row.category,
          unit: row.unit,
          reorderLevel: row.reorderLevel,
          supplier: row.supplier,
          preferredSupplier: row.supplier,
          description: row.description,
        };
        const legacy = {
          name: row.name,
          quantity: row.quantity,
          perItemCost: row.perItemCost,
          description: row.description,
        };
        try {
          await graphqlRequest(CREATE_ITEM, { createItemInput: full });
          ok += 1;
        } catch (err) {
          const msg = String(err?.message || '').toLowerCase();
          if (
            msg.includes('category') ||
            msg.includes('unit') ||
            msg.includes('reorder') ||
            msg.includes('supplier') ||
            msg.includes('unknown') ||
            msg.includes('maxstock') ||
            msg.includes('preferred')
          ) {
            try {
              await graphqlRequest(CREATE_ITEM, { createItemInput: legacy });
              ok += 1;
              continue;
            } catch (e2) {
              errors.push(`${row.name}: ${e2?.message || 'failed'}`);
              continue;
            }
          }
          errors.push(`${row.name}: ${err?.message || 'failed'}`);
        }
      }

      if (ok > 0) {
        toast.success(
          `Added ${ok} product${ok === 1 ? '' : 's'}${
            errors.length ? ` (${errors.length} failed)` : ''
          }`,
        );
        onSaved?.();
        if (errors.length === 0) onClose?.();
      } else {
        toast.error(errors[0] || 'Failed to add products');
      }
      if (errors.length && ok > 0) {
        toast.warn(errors.slice(0, 3).join(' · '));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ap-overlay" onClick={onClose}>
      <div
        className="ap-modal ap-modal--wide"
        role="dialog"
        aria-labelledby="bulk-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ap-header">
          <h2 id="bulk-title">Bulk Add Products</h2>
          <button
            type="button"
            className="ap-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </header>

        <form className="ap-form" onSubmit={handleSubmit}>
          <p className="ip-bulk-hint">
            Enter multiple products below. Empty name rows are skipped. SKU codes
            are generated automatically.
          </p>

          <div className="ip-bulk-table-wrap">
            <table className="ip-bulk-table">
              <thead>
                <tr>
                  <th>Product Name *</th>
                  <th>Category *</th>
                  <th>Unit *</th>
                  <th>Cost *</th>
                  <th>Qty *</th>
                  <th>Reorder</th>
                  <th>Supplier</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <input
                        value={row.name}
                        onChange={(e) =>
                          updateRow(row.key, 'name', e.target.value)
                        }
                        placeholder="Product name"
                      />
                    </td>
                    <td>
                      <select
                        value={row.category}
                        onChange={(e) =>
                          updateRow(row.key, 'category', e.target.value)
                        }
                      >
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        value={row.unit}
                        onChange={(e) =>
                          updateRow(row.key, 'unit', e.target.value)
                        }
                      >
                        {UNITS.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.perItemCost}
                        onChange={(e) =>
                          updateRow(row.key, 'perItemCost', e.target.value)
                        }
                        placeholder="0.00"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={row.quantity}
                        onChange={(e) =>
                          updateRow(row.key, 'quantity', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={row.reorderLevel}
                        onChange={(e) =>
                          updateRow(row.key, 'reorderLevel', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        value={row.supplier}
                        onChange={(e) =>
                          updateRow(row.key, 'supplier', e.target.value)
                        }
                        placeholder="Optional"
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="ip-bulk-remove"
                        title="Remove row"
                        onClick={() => removeRow(row.key)}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button type="button" className="ip-bulk-add-row" onClick={addRow}>
            <FaPlus /> Add row
          </button>

          <div className="ap-footer ip-bulk-footer">
            <span className="ip-bulk-count">
              {filledCount} product{filledCount === 1 ? '' : 's'} ready
            </span>
            <div className="ip-bulk-actions">
              <button type="button" className="ap-btn" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="ap-btn ap-btn--primary"
                disabled={saving}
              >
                {saving ? 'Adding…' : 'Add Products'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
